import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Surface, Lang } from '@crosscastr/shared';
import { api } from '../lib/api.js';
import { fmt } from '../lib/format.js';
import { Icon, type IconName } from '../components/Icon.js';

type ScheduleType = 'one_time' | 'scheduled' | 'recurring';
type PreviewTab = 'wa' | 'status' | 'tg' | 'x';

const SURFACES: { key: Surface; name: string; glyph: string; icon: IconName; addon?: boolean }[] = [
  { key: 'wa_group', name: 'קבוצות WhatsApp', glyph: 'g-wa', icon: 'wa' },
  { key: 'wa_status', name: 'סטטוס WhatsApp', glyph: 'g-status', icon: 'status' },
  { key: 'wa_channel', name: 'ערוץ WhatsApp', glyph: 'g-channel', icon: 'channel' },
  { key: 'telegram', name: 'Telegram', glyph: 'g-tg', icon: 'telegram' },
  { key: 'x', name: 'X / Twitter', glyph: 'g-x', icon: 'x', addon: true },
];


// Auto-translate (WOW #2): HE is the source; AR + RU are the one-click fan-out languages.
const EXTRA_LANGS: Lang[] = ['ar', 'ru'];
const LANG_LABEL: Record<Lang, string> = { he: 'עברית', ar: 'ערבית', ru: 'רוסית' };

/** Render a sample of the message for preview: personalization + first spintax option. The {קבוצה}
 * token resolves to the customer's own first real group name (or a neutral placeholder before any
 * group is connected) — never a fixed demo name. */
function renderSample(text: string, groupName: string): string {
  return text
    .replace(/\{שם\}/g, 'משה')
    .replace(/\{קבוצה\}/g, groupName)
    .replace(/\{תאריך\}/g, new Date().toLocaleDateString('he-IL'))
    .replace(/\{([^{}]*\|[^{}]*)\}/g, (_m, g: string) => g.split('|')[0]);
}

function countSpintax(text: string): number {
  const groups = [...text.matchAll(/\{([^{}]*\|[^{}]*)\}/g)];
  return groups.reduce((prod, m) => prod * m[1].split('|').length, 1);
}

/** Live estimate of how long the protected (jittered ~6s/group) broadcast will take. */
function paceLabel(groups: number): string {
  const secs = Math.max(1, groups) * 6; // ~6s average spacing per group
  if (secs < 90) return `${secs} שנ'`;
  return `${Math.ceil(secs / 60)} דק'`;
}

export function ComposerPage(): JSX.Element {
  const navigate = useNavigate();
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState('');
  const [selected, setSelected] = useState<Set<Surface>>(new Set(['wa_group', 'wa_status', 'wa_channel', 'telegram']));
  const [schedule, setSchedule] = useState<ScheduleType>('one_time');
  const [scheduledAt, setScheduledAt] = useState('');
  const [tab, setTab] = useState<PreviewTab>('wa');
  const [variants, setVariants] = useState<string[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [articleUrl, setArticleUrl] = useState('');

  // Progressive disclosure: everything beyond text + channels + send lives behind this.
  const [advanced, setAdvanced] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Multilingual per-language fan-out state (WOW #2).
  const [multiLang, setMultiLang] = useState(false);
  const [langOn, setLangOn] = useState<Set<Lang>>(new Set(EXTRA_LANGS));
  const [langText, setLangText] = useState<Record<Lang, string>>({ he: '', ar: '', ru: '' });
  const [langGroups, setLangGroups] = useState<Record<Lang, Set<string>>>({ he: new Set(), ar: new Set(), ru: new Set() });

  const { data: targets } = useQuery({ queryKey: ['targets'], queryFn: () => api.targets() });
  const waGroups = useMemo(() => (targets ?? []).filter((t) => t.surface === 'wa_group'), [targets]);
  const allGroupIds = useMemo(() => waGroups.map((g) => g.id), [waGroups]);
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api.me() });

  // Per-send group picker (default view, single-language path). null = "all groups selected" (the
  // default) so a freshly-loaded list is fully on without an effect; a concrete Set once the user picks.
  const [pickedGroups, setPickedGroups] = useState<Set<string> | null>(null);
  const pickedGroupIds = useMemo(
    () => (pickedGroups ?? new Set(allGroupIds)),
    [pickedGroups, allGroupIds],
  );
  function toggleGroup(id: string): void {
    setPickedGroups((prev) => {
      const base = new Set(prev ?? allGroupIds);
      if (base.has(id)) base.delete(id);
      else base.add(id);
      return base;
    });
  }

  // "המספר שלי" — the number the real test-send DMs (E.164 / 0-prefixed). Empty → the API falls back
  // to the tenant's sync-group admin phone if one is set.
  const [testPhone, setTestPhone] = useState('');

  // Best-time-to-send suggestion (WOW wave #2): recommend an optimal scheduled slot from analytics.
  const { data: bestTime } = useQuery({ queryKey: ['bestTime'], queryFn: () => api.bestTime() });

  const translateMutation = useMutation({
    mutationFn: () => api.translate(text, [...langOn]),
    onSuccess: (r) => {
      setLangText((prev) => {
        const next = { ...prev, he: text };
        for (const t of r.translations) next[t.lang] = t.text;
        return next;
      });
      setToast(`תורגם ל-${r.translations.length} שפות`);
    },
  });

  function toggleLang(l: Lang): void {
    setLangOn((prev) => { const n = new Set(prev); if (n.has(l)) n.delete(l); else n.add(l); return n; });
  }
  function toggleLangGroup(l: Lang, id: string): void {
    setLangGroups((prev) => {
      const n = new Set(prev[l]);
      if (n.has(id)) n.delete(id); else n.add(id);
      return { ...prev, [l]: n };
    });
  }
  /** Build the per-language legs for the API (only languages that have both text AND groups). */
  function buildLanguages(): { lang: Lang; text: string; targetIds: string[] }[] | undefined {
    if (!multiLang) return undefined;
    const legs: { lang: Lang; text: string; targetIds: string[] }[] = [];
    const activeLangs: Lang[] = ['he', ...[...langOn]];
    for (const l of activeLangs) {
      const ids = [...langGroups[l]];
      const body = l === 'he' ? text : langText[l]?.trim();
      if (ids.length && body) legs.push({ lang: l, text: body, targetIds: ids });
    }
    return legs.length ? legs : undefined;
  }

  const aiMutation = useMutation({
    mutationFn: () => api.aiVariations(text, 10),
    onSuccess: (r) => { setVariants(r.variants); setToast(`נוצרו ${r.variants.length} ניסוחים שונים`); },
  });

  // Summarize-to-broadcast (WOW wave #2): paste an article URL → fill the composer with a short
  // broadcast blurb; when multi-language is on, also fill the per-language bodies.
  const summarizeMutation = useMutation({
    mutationFn: () => api.summarize(articleUrl.trim(), multiLang ? [...langOn] : undefined),
    onSuccess: (r) => {
      setText(r.summary);
      if (r.translations?.length) {
        setLangText((prev) => {
          const next = { ...prev, he: r.summary };
          for (const t of r.translations!) next[t.lang] = t.text;
          return next;
        });
      }
      setToast(`הכתבה סוכמה${r.translations?.length ? ` · ${r.translations.length} שפות` : ''}`);
    },
    onError: () => setToast('סיכום הכתבה נכשל — בדקו את הקישור'),
  });
  const createMutation = useMutation({
    mutationFn: (opts: { forTest: boolean }) =>
      api.createCampaign({
        name: text.split('\n')[0].slice(0, 60) || 'הודעה חדשה',
        content: { text },
        surfaces: [...selected],
        languages: opts.forTest ? undefined : buildLanguages(),
        // Per-send group picker: scope the wa_group leg to exactly the chosen groups (single-language
        // path only — the multi-language legs carry their own targetIds). Skipped for the test-send.
        groupTargetIds:
          opts.forTest || multiLang || !selected.has('wa_group') ? undefined : [...pickedGroupIds],
        schedule: opts.forTest
          ? { type: 'scheduled', at: new Date(Date.now() + 86_400_000).toISOString() }
          : { type: schedule, at: schedule === 'scheduled' && scheduledAt ? new Date(scheduledAt).toISOString() : undefined },
      }),
  });

  const reach = useMemo(() => {
    if (!targets) return { groups: 0, total: 0 };
    const rows = targets.filter((t) => selected.has(t.surface));
    // In the default (single-language) view the wa_group leg is scoped to the picked groups.
    const scoped = multiLang
      ? rows
      : rows.filter((t) => t.surface !== 'wa_group' || pickedGroupIds.has(t.id));
    const groups = scoped.filter((t) => t.surface === 'wa_group').length;
    const total = scoped.reduce((s, t) => s + t.memberCount, 0);
    return { groups, total };
  }, [targets, selected, multiLang, pickedGroupIds]);

  const spintaxCombos = countSpintax(text);
  const remaining = 1024 - text.length;

  // Preview reflects the CUSTOMER'S real data — their first connected group (name + real member count
  // + its saved join-link suffix), falling back to the tenant's own name, then a neutral placeholder.
  const previewGroup = waGroups[0];
  const tgTarget = useMemo(() => (targets ?? []).find((t) => t.surface === 'telegram'), [targets]);
  const xTarget = useMemo(() => (targets ?? []).find((t) => t.surface === 'x'), [targets]);
  const previewAudience = previewGroup?.name ?? me?.tenant.name ?? 'הקבוצה שלך';
  const previewMembers = previewGroup ? fmt(previewGroup.memberCount) : null;
  const previewSuffix = (previewGroup?.suffix ?? '').trim();
  const previewInitials = (previewAudience.trim() || '·').slice(0, 2);
  const sample = renderSample(text, previewAudience);

  function insert(token: string): void {
    const el = editorRef.current;
    if (!el) { setText((t) => t + token); return; }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setText((t) => t.slice(0, start) + token + t.slice(end));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  }

  /** Format a Date as a `datetime-local` value (local time, yyyy-MM-ddTHH:mm). */
  function toLocalInput(d: Date): string {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  /** Next calendar occurrence of (dow, hour) at :00, in the browser's local time. */
  function applyBestSlot(dow: number, hour: number): void {
    const now = new Date();
    const d = new Date(now);
    d.setHours(hour, 0, 0, 0);
    let add = (dow - now.getDay() + 7) % 7;
    if (add === 0 && d.getTime() <= now.getTime()) add = 7;
    d.setDate(d.getDate() + add);
    setSchedule('scheduled');
    setScheduledAt(toLocalInput(d));
  }

  function toggleSurface(s: Surface): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  async function onSend(): Promise<void> {
    if (!selected.size) { setToast('בחר לפחות ערוץ אחד'); return; }
    if (multiLang && !buildLanguages()) { setToast('בחרו קבוצות לכל שפה פעילה'); return; }
    if (!multiLang && selected.has('wa_group') && pickedGroupIds.size === 0) { setToast('בחר לפחות קבוצה אחת'); return; }
    const c = await createMutation.mutateAsync({ forTest: false });
    // Honest confirmation: if the tenant's send window ("מתי לשלוח?") holds this send until morning,
    // say so — no silent "sent" that the user later thinks failed. Otherwise a plain "נשלח ✅".
    let delay = 900;
    if (schedule === 'scheduled') {
      setToast('ההודעה תוזמנה ✓');
    } else if (c.timing === 'deferred' && c.deferUntil) {
      const at = new Date(c.deferUntil);
      const day = at.toDateString() === new Date().toDateString() ? 'היום' : 'מחר';
      const hh = String(at.getHours()).padStart(2, '0');
      setToast(`✅ ההודעה תישלח ${day} בשעה ${hh}:00 (מחוץ לשעות השליחה שהגדרת)`);
      delay = 2600;
    } else {
      setToast('נשלח ✅');
    }
    setTimeout(() => navigate('/reports'), delay);
  }
  async function onTest(): Promise<void> {
    try {
      const c = await createMutation.mutateAsync({ forTest: true });
      const r = await api.testSend(c.id, testPhone.trim() || undefined);
      setToast(`נשלחה בדיקה אל ${r.phoneHint ?? 'המספר שלך'} ✓`);
    } catch (err) {
      const msg = String((err as Error)?.message ?? '');
      if (msg.includes('no_session')) setToast('אין מספר מחובר — חבר מספר ב’המספרים והקבוצות’');
      else if (msg.includes('no_test_number')) setToast('הזן את המספר שלך לשליחת בדיקה');
      else setToast('שליחת הבדיקה נכשלה');
    }
  }

  return (
    <>
      <div className="content compose-simple">
        <div className="page-head">
          <div>
            <h1>שליחת הודעה</h1>
            <div className="sub">כתוב פעם אחת — יופץ לכל הערוצים שתבחר.</div>
          </div>
        </div>

        {/* Friendly phone-path shortcut — the even-simpler way to broadcast. */}
        <div className="info-banner">
          <Icon name="wa" />
          <span>
            אפשר גם לשלוח ישר מהוואטסאפ — כתוב הודעה בקבוצת השליטה שלך והיא תופץ אוטומטית.{' '}
            <Link to="/sessions">הגדרה ב'המספרים והקבוצות' ←</Link>
          </span>
        </div>

        {/* 1 — WHAT to send */}
        <section className="compose-block">
          <h2>מה לשלוח?</h2>
          <div className="editor-card">
            <textarea ref={editorRef} className="editor-body" value={text} onChange={(e) => setText(e.target.value)} dir="rtl" placeholder="כתוב כאן את ההודעה שתופץ… אפשר להוסיף {שם} כדי לפנות לכל אחד בשמו" />
            <div className="editor-foot">
              <button type="button" className="tool var" onClick={() => insert('{שם}')}><Icon name="variable" /> הוסף שם</button>
              <span className="p-tip">אפשר להוסיף <b>{'{שם}'}</b> — כל אחד יקבל את ההודעה עם שמו</span>
              <span style={{ marginInlineStart: 'auto' }}>נותרו <span className="mono">{remaining}</span> תווים</span>
            </div>
          </div>
        </section>

        {/* 2 — WHERE to send */}
        <section className="compose-block">
          <h2>לאן לשלוח?</h2>
          <div className="chan-chips">
            {SURFACES.map((s) => (
              <button
                key={s.key}
                type="button"
                className={`chan-chip ${selected.has(s.key) ? 'on' : ''}`}
                onClick={() => toggleSurface(s.key)}
              >
                <span className={`glyph ${s.glyph}`}><Icon name={s.icon} /></span>
                <span className="cl">{s.name}{s.addon ? ' · בתשלום' : ''}</span>
                {selected.has(s.key) && <span className="ck"><Icon name="check" /></span>}
              </button>
            ))}
          </div>

          {/* Per-send group picker — pick exactly which WhatsApp groups THIS message goes to. Shown
              in the default view whenever the groups channel is on (the multi-language flow has its
              own per-language pickers in advanced). */}
          {selected.has('wa_group') && !multiLang && (
            <div className="group-picker" style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <b style={{ fontSize: 13 }}>אילו קבוצות? <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>({pickedGroupIds.size}/{waGroups.length})</span></b>
                {waGroups.length > 0 && (
                  <button
                    type="button"
                    className="tool"
                    style={{ fontSize: 12 }}
                    onClick={() => setPickedGroups(pickedGroupIds.size === allGroupIds.length ? new Set() : new Set(allGroupIds))}
                  >
                    {pickedGroupIds.size === allGroupIds.length ? 'נקה הכל' : 'בחר הכל'}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {waGroups.length === 0 && (
                  <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                    אין קבוצות WhatsApp מחוברות — הגדר ב’המספרים והקבוצות’.
                  </span>
                )}
                {waGroups.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => toggleGroup(g.id)}
                    className={`tool ${pickedGroupIds.has(g.id) ? 'var' : ''}`}
                    style={{ fontSize: 12 }}
                  >
                    {pickedGroupIds.has(g.id) ? '✓ ' : ''}{g.name} · {fmt(g.memberCount)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="reach-line">{reach.groups} קבוצות · ~{fmt(reach.total)} יראו</div>
        </section>

        {/* 3 — SEND */}
        <div className="send-row">
          <input
            dir="ltr"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="המספר שלי (05…)"
            aria-label="המספר שלי לשליחת בדיקה"
            style={{ width: 150, fontSize: 13, padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--hair)', background: 'var(--surface-1)', color: 'var(--text)' }}
          />
          <button className="btn ghost" onClick={onTest} disabled={createMutation.isPending}><Icon name="send" /> שליחת בדיקה אליי</button>
          <button className="btn btn-send sheen-host sheen-hover" onClick={onSend} disabled={createMutation.isPending}>
            <Icon name="send" /> שלח <span className="cost">· {selected.size} ערוצים · ~{paceLabel(reach.groups)}</span>
          </button>
        </div>

        {/* Everything advanced — collapsed by default */}
        <section className="adv">
          <button type="button" className="adv-head" onClick={() => setAdvanced((v) => !v)} aria-expanded={advanced}>
            <Icon name="settings" /> אפשרויות מתקדמות
            <span className={`chev ${advanced ? 'open' : ''}`}><Icon name="chevronDown" /></span>
          </button>

          {advanced && (
            <div className="adv-body">
              {/* Scheduling */}
              <div className="variants">
                <div className="vh"><Icon name="clock" /> תזמון</div>
                <p>מתי לשלוח — עכשיו, במועד עתידי, או שליחה חוזרת.</p>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
                  <div className="sched">
                    <button className={schedule === 'one_time' ? 'on' : ''} onClick={() => setSchedule('one_time')}><Icon name="bolt" /> עכשיו</button>
                    <button className={schedule === 'scheduled' ? 'on' : ''} onClick={() => setSchedule('scheduled')}><Icon name="calendar" /> מתוזמן</button>
                    <button className={schedule === 'recurring' ? 'on' : ''} onClick={() => setSchedule('recurring')}><Icon name="repeat" /> חוזר</button>
                  </div>
                  {bestTime?.best && (
                    <button
                      type="button"
                      className="tool var"
                      title="השעה שבה הכי הרבה אנשים פותחים"
                      onClick={() => applyBestSlot(bestTime.best!.dow, bestTime.best!.hour)}
                    >
                      <Icon name="clock" /> מומלץ: {bestTime.best.label}
                    </button>
                  )}
                  {schedule === 'scheduled' && (
                    <label className="sched-at">מתי: <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} /></label>
                  )}
                </div>
              </div>

              {/* Text variations + formatting */}
              <div className="variants" style={{ marginTop: 14 }}>
                <div className="vh"><Icon name="sparkles" /> וריאציות ועיצוב טקסט · הגנה מחסימה</div>
                <p>כל נמען מקבל ניסוח מעט אחר <span className="count">(~{spintaxCombos})</span> — כך פחות סיכוי שההודעה תיחשב כספאם.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  <button className="tool" onClick={() => insert('*מודגש*')}><Icon name="bold" /> מודגש</button>
                  <button className="tool" onClick={() => insert('_נטוי_')}><Icon name="italic" /> נטוי</button>
                  <button className="tool" onClick={() => insert('\n• ')}><Icon name="list" /> רשימה</button>
                  <button className="tool spin" onClick={() => insert('{אפשרות א|אפשרות ב}')}><Icon name="spin" /> וריאציה</button>
                  <button className="tool ai" style={{ marginInlineStart: 'auto' }} onClick={() => aiMutation.mutate()} disabled={aiMutation.isPending}>
                    <Icon name="sparkles" /> {aiMutation.isPending ? 'יוצר…' : 'צור 10 וריאציות'}
                  </button>
                </div>
                {variants.length > 0 && (
                  <ul className="variant-list">
                    {variants.slice(0, 6).map((v, i) => <li key={i}>{v.replace(/\n/g, ' ')}</li>)}
                  </ul>
                )}
              </div>

              {/* Summarize-to-broadcast — article URL → broadcast blurb (+ 3 languages) */}
              <div className="variants" style={{ marginTop: 14 }}>
                <div className="vh"><Icon name="sparkles" /> סיכום כתבה מקישור</div>
                <p>הדביקו קישור לכתבה — נשלוף את הטקסט, נסכם אותו להודעה קצרה וימלא את תיבת הכתיבה. אם שליחה בכמה שפות פעילה, גם נתרגם לכל שפה.</p>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    dir="ltr"
                    value={articleUrl}
                    onChange={(e) => setArticleUrl(e.target.value)}
                    placeholder="https://…"
                    style={{ flex: 1, fontSize: 13, padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--hair)', background: 'var(--surface-1)', color: 'var(--text)' }}
                  />
                  <button
                    type="button"
                    className="tool ai"
                    onClick={() => summarizeMutation.mutate()}
                    disabled={summarizeMutation.isPending || !articleUrl.trim()}
                  >
                    <Icon name="sparkles" /> {summarizeMutation.isPending ? 'מסכם…' : 'סכם'}
                  </button>
                </div>
              </div>

              {/* Auto-translate HE/AR/RU + per-language fan-out */}
              <div className="variants" style={{ marginTop: 14 }}>
                <div className="vh" style={{ justifyContent: 'space-between' }}>
                  <span><Icon name="sparkles" /> תרגום לערבית / רוסית</span>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={multiLang} onChange={(e) => setMultiLang(e.target.checked)} /> הפעל
                  </label>
                </div>
                <p>כותבים פעם אחת בעברית, ובלחיצה מתרגמים לערבית ולרוסית — כל שפה נשלחת לקבוצות שלה.</p>

                {multiLang && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                      {EXTRA_LANGS.map((l) => (
                        <button key={l} type="button" className={`tool ${langOn.has(l) ? 'var' : ''}`} onClick={() => toggleLang(l)}>
                          {langOn.has(l) ? '✓ ' : ''}{LANG_LABEL[l]}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="tool ai"
                        style={{ marginInlineStart: 'auto' }}
                        onClick={() => translateMutation.mutate()}
                        disabled={translateMutation.isPending || langOn.size === 0}
                      >
                        <Icon name="sparkles" /> {translateMutation.isPending ? 'מתרגם…' : 'תרגם לכל השפות'}
                      </button>
                    </div>

                    {(['he', ...EXTRA_LANGS] as Lang[])
                      .filter((l) => l === 'he' || langOn.has(l))
                      .map((l) => (
                        <div key={l} style={{ borderTop: '1px solid var(--hair)', paddingTop: 10, marginTop: 10 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <b style={{ fontSize: 13 }}>{LANG_LABEL[l]}{l === 'he' ? ' · מקור' : ''}</b>
                            <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{langGroups[l].size} קבוצות</span>
                          </div>
                          {l !== 'he' && (
                            <textarea
                              dir={l === 'ar' ? 'rtl' : 'ltr'}
                              value={langText[l]}
                              onChange={(e) => setLangText((p) => ({ ...p, [l]: e.target.value }))}
                              placeholder={`תרגום ל${LANG_LABEL[l]} יופיע כאן (ניתן לעריכה)`}
                              style={{ width: '100%', minHeight: 54, fontSize: 13, padding: 8, borderRadius: 'var(--r-sm)', border: '1px solid var(--hair)', background: 'var(--surface-1)', color: 'var(--text)', resize: 'vertical', marginBottom: 8 }}
                            />
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {waGroups.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>אין קבוצות WhatsApp מוגדרות</span>}
                            {waGroups.map((g) => (
                              <button
                                key={g.id}
                                type="button"
                                onClick={() => toggleLangGroup(l, g.id)}
                                className={`tool ${langGroups[l].has(g.id) ? 'var' : ''}`}
                                style={{ fontSize: 12 }}
                              >
                                {langGroups[l].has(g.id) ? '✓ ' : ''}{g.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Live preview — off by default */}
              <div className="variants" style={{ marginTop: 14 }}>
                <div className="vh" style={{ justifyContent: 'space-between' }}>
                  <span><Icon name="eye" /> תצוגה מקדימה</span>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={showPreview} onChange={(e) => setShowPreview(e.target.checked)} /> הצג תצוגה מקדימה
                  </label>
                </div>

                {showPreview && (
                  <div style={{ marginTop: 12 }}>
                    <div className="ptabs">
                      <button className={`ptab ${tab === 'wa' ? 'on' : ''}`} onClick={() => setTab('wa')}><span className="dt" style={{ background: '#128C7E' }} /> קבוצה</button>
                      <button className={`ptab ${tab === 'status' ? 'on' : ''}`} onClick={() => setTab('status')}><span className="dt" style={{ background: '#25D366' }} /> סטטוס</button>
                      <button className={`ptab ${tab === 'tg' ? 'on' : ''}`} onClick={() => setTab('tg')}><span className="dt" style={{ background: '#2AABEE' }} /> Telegram</button>
                      <button className={`ptab ${tab === 'x' ? 'on' : ''}`} onClick={() => setTab('x')}><span className="dt" style={{ background: '#888' }} /> X</button>
                    </div>

                    {tab === 'wa' && (
                      <div>
                        <div className="phone-frame">
                          <div className="wa-head"><div className="av">{previewInitials}</div><div><div className="t">{previewAudience}</div><div className="s">{previewMembers ? `${previewMembers} חברים` : 'קבוצת פרסום'}</div></div></div>
                          <div className="wa-body">
                            <div className="bubble">
                              {sample || 'תצוגה מקדימה של ההודעה שלך תופיע כאן'}
                              {previewSuffix && <span className="suffix ltr">{previewSuffix}</span>}
                              <span className="time ltr">08:14 ✓✓</span>
                            </div>
                          </div>
                        </div>
                        <div className="preview-note"><b>{'{שם}'}</b> הוחלף בשם לדוגמה{previewSuffix ? ' · הטקסט הקבוע בסוף נוסף אוטומטית' : ''}</div>
                      </div>
                    )}
                    {tab === 'status' && (
                      <div>
                        <div className="status-card">
                          <div className="sc-logo"><span className="lm">{previewInitials}</span> {previewAudience}</div>
                          <div className="sc-body"><h3>{sample.split('\n').find((l) => l.trim()) ?? sample}</h3></div>
                          <div className="sc-foot ltr">08:14</div>
                        </div>
                        <div className="preview-note">הטקסט <b>הופך אוטומטית</b> לתמונת סטטוס</div>
                      </div>
                    )}
                    {tab === 'tg' && (
                      <div>
                        <div className="phone-frame">
                          <div className="tg-head"><div className="av">{previewInitials}</div><div><div className="t" style={{ fontWeight: 600, fontSize: 14 }}>{tgTarget?.name ?? previewAudience}</div></div></div>
                          <div className="tg-body">
                            <div className="tg-bubble">{sample || 'תצוגה מקדימה של ההודעה שלך תופיע כאן'}{previewSuffix && <><br /><span className="link ltr">{previewSuffix}</span></>}</div>
                          </div>
                        </div>
                        <div className="preview-note">בערוץ אין התאמה אישית · קישור נפרד לטלגרם</div>
                      </div>
                    )}
                    {tab === 'x' && (
                      <div>
                        <div className="x-card">
                          <div className="xh"><div className="xa" /><div><div className="xn">{xTarget?.name ?? previewAudience}</div></div></div>
                          <div className="xtxt">{sample.slice(0, 240)}{previewSuffix && <> <span className="link ltr">{previewSuffix}</span></>}</div>
                          <div className="x-cost"><Icon name="clock" /> תוספת תשלום · ‎$0.20 לפוסט · נקצץ ל-280 תווים</div>
                        </div>
                        <div className="preview-note">X נמכר בתוספת תשלום · המחיר מוצג לפני שליחה</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {toast && <Toast text={toast} onDone={() => setToast(null)} />}
    </>
  );
}

function Toast({ text, onDone }: { text: string; onDone: () => void }): JSX.Element {
  setTimeout(onDone, 2600);
  return <div className="toast"><span className="d" /> {text}</div>;
}
